/**
 * Small popovers and menus: Escape or a press outside `ref` closes them; Escape puts focus back on the button that
 * opened it (the element in `ref` with aria-expanded).
 */
import { useEffect, useRef, type RefObject } from "react";

export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  const fn = useRef(close);
  fn.current = close;
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) fn.current(); };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      ref.current?.querySelector<HTMLElement>("[aria-expanded]")?.focus();
      fn.current();
    };
    addEventListener("pointerdown", down, true);
    addEventListener("keydown", key, true);
    return () => { removeEventListener("pointerdown", down, true); removeEventListener("keydown", key, true); };
  }, [ref, open]);
}
